class ConstructorBrowse extends ConstructorFilteredPaginatedBase {
  _surfaceName() {
    return 'browse';
  }

  _sortRadioIdPrefix() {
    return 'ConstructorSortBy-';
  }

  connectedCallback() {
    this.productsContainer = this.querySelector('.js-container-for-products');
    this.productsToShow = parseInt(this.getAttribute('products-to-show'), 10) || 24;
    this.filterName = this.getAttribute('filter-name') || 'group_id';
    this.filterValue = this.getAttribute('filter-value') || '';
    this.itemClasses = (this.getAttribute('item-class') || '').trim().split(/\s+/).filter(Boolean);
    this.fallbackUrl = this.getAttribute('fallback-url') || '';
    this._emptyState = this.querySelector('.js-constructor-empty-state');

    this._initFilterAndSort();

    this._banners = [...this.querySelectorAll('.js-promo-banner')].sort((a, b) => {
      const pageDiff = parseInt(a.dataset.bannerPage, 10) - parseInt(b.dataset.bannerPage, 10);
      if (pageDiff !== 0) return pageDiff;
      return parseInt(a.dataset.bannerPosition, 10) - parseInt(b.dataset.bannerPosition, 10);
    });
    this._fetchedProductCount = 0;
    this.classList.add('is-observed');
    this._initPagination();

    if (this.filterValue) {
      this._load();
    } else {
      console.warn('[ConstructorBrowse] No filter-value attribute set — skipping.');
    }
  }

  _hasMore() {
    return this._fetchedProductCount < this._totalNumResults;
  }

  _bannersForPage(page) {
    return this._banners.filter((b) => parseInt(b.dataset.bannerPage, 10) === page);
  }

  // Counts the visual grid slots consumed by banners on a page.
  // A double-width banner spans 2 columns so it displaces 2 product slots.
  _bannerSlotCount(page) {
    return this._bannersForPage(page).reduce((sum, b) => sum + (b.dataset.bannerDoubleWidth === 'true' ? 2 : 1), 0);
  }

  _resolveProductsLimit(handles) {
    // Deep-link: fetch products for all pages 1..N, not just the current page.
    if (this._isInitialRender && this.currentPage > 1) {
      let totalNeeded = 0;
      for (let p = 1; p <= this.currentPage; p++) {
        totalNeeded += Math.max(1, this.productsToShow - this._bannerSlotCount(p));
      }
      return Math.min(handles.length, totalNeeded);
    }
    // Capping at productsToShow keeps the slot count predictable for banner injection.
    return Math.min(handles.length, this.productsToShow);
  }

  _getPaginationParams() {
    // Deep-link (?page=N): fetch all products for pages 1..N in one shot.
    if (this._isInitialRender && this.currentPage > 1) {
      let totalNeeded = 0;
      for (let p = 1; p <= this.currentPage; p++) {
        totalNeeded += Math.max(1, this.productsToShow - this._bannerSlotCount(p));
      }
      return { offset: 0, resultsPerPage: totalNeeded };
    }

    // Use exact offset to prevent Constructor skipping items when resultsPerPage varies per page.
    const resultsPerPage = Math.max(1, this.productsToShow - this._bannerSlotCount(this.currentPage));
    return { offset: this._fetchedProductCount, resultsPerPage };
  }

  _renderHtml(htmlText) {
    const isDeepLink = this._isInitialRender && this.currentPage > 1;
    const isReplace = this.currentPage === 1 || this._isInitialRender;

    // Capture before super._renderHtml() — it clears the container and sets _isInitialRender = false.
    const pageStartIndex = isReplace ? 0 : this.productsContainer.children.length;

    super._renderHtml(htmlText);

    if (isDeepLink) {
      // Each page has (productsToShow - doubleWidthBannersOnPage) DOM nodes after injection
      // (double-width banners are 1 DOM node but 2 visual slots), so track DOM offset explicitly.
      let domOffset = 0;
      for (let p = 1; p <= this.currentPage; p++) {
        this._injectBannersForPage(p, domOffset);
        const doubleWidthCount = this._bannersForPage(p).filter((b) => b.dataset.bannerDoubleWidth === 'true').length;
        domOffset += this.productsToShow - doubleWidthCount;
      }
    } else {
      this._injectBannersForPage(this.currentPage, pageStartIndex);
    }
  }

  _injectBannersForPage(page, pageStartIndex = 0) {
    const banners = this._bannersForPage(page);
    if (banners.length === 0) return;

    // `position` is a visual slot index, but absoluteIndex is a DOM index.
    // Each double-width banner already inserted is 1 DOM node but 2 visual slots,
    // so subtract 1 from absoluteIndex for every such banner inserted before this one.
    let extraSlots = 0;
    banners.forEach((banner) => {
      const position = parseInt(banner.dataset.bannerPosition, 10);
      if (!position || position < 1) {
        console.warn('[ConstructorBrowse] Skipping banner with invalid position:', banner.dataset.bannerPosition);
        return;
      }
      const absoluteIndex = pageStartIndex + position - 1 - extraSlots;
      const refItem = this.productsContainer.children[absoluteIndex] ?? null;
      banner.hidden = false;
      if (refItem) {
        this.productsContainer.insertBefore(banner, refItem);
      } else {
        this.productsContainer.appendChild(banner);
      }
      if (banner.dataset.bannerDoubleWidth === 'true') extraSlots += 1;
    });
  }

  async _fetchConstructorResults() {
    if (!window.constructorio?.browse?.getBrowseResults) {
      throw new Error('constructorio.browse.getBrowseResults is not available');
    }

    const params = this._applyFilterAndSortTo({ ...this._getPaginationParams() });

    const result = await window.constructorio.browse.getBrowseResults(this.filterName, this.filterValue, params);
    this._totalNumResults = result?.response?.total_num_results ?? 0;
    const returnedCount = result?.response?.results?.length ?? 0;
    // Use the smaller of returned vs requested so _fetchedProductCount matches the actual Constructor offset.
    this._fetchedProductCount += Math.min(returnedCount, params.resultsPerPage);

    // Sync beacon attributes with the real Constructor data now that we have it.
    const resultsEl = this.querySelector('[data-cnstrc-browse]');
    if (resultsEl) {
      resultsEl.setAttribute('data-cnstrc-num-results', this._totalNumResults);
      resultsEl.setAttribute('data-cnstrc-result-page', this.currentPage);
    }

    return result;
  }

  // Prefetch next page without mutating _fetchedProductCount / beacons — applied on consume.
  async _requestConstructorForPrefetch(nextPage) {
    if (!window.constructorio?.browse?.getBrowseResults) return null;

    const resultsPerPage = Math.max(1, this.productsToShow - this._bannerSlotCount(nextPage));
    const params = this._applyFilterAndSortTo({
      offset: this._fetchedProductCount,
      resultsPerPage,
    });

    const result = await window.constructorio.browse.getBrowseResults(this.filterName, this.filterValue, params);
    const returnedCount = result?.response?.results?.length ?? 0;
    const totalNumResults = result?.response?.total_num_results ?? 0;
    const fetchedDelta = Math.min(returnedCount, params.resultsPerPage);

    return {
      result,
      apply: () => {
        this._totalNumResults = totalNumResults;
        this._fetchedProductCount += fetchedDelta;
        const resultsEl = this.querySelector('[data-cnstrc-browse]');
        if (resultsEl) {
          resultsEl.setAttribute('data-cnstrc-num-results', this._totalNumResults);
          resultsEl.setAttribute('data-cnstrc-result-page', this.currentPage);
        }
      },
    };
  }

  _onNoResults() {
    if (this.productsContainer) this.productsContainer.innerHTML = '';
    if (this.paginationContainer) this.paginationContainer.innerHTML = '';
    if (this._emptyState) this._emptyState.hidden = false;
  }

  async _onError() {
    console.error('[ConstructorBrowse] Failed to load — falling back to Shopify collection.');
    if (!(await this._loadNativeSectionFallback())) this._onNoResults();
  }
}

customElements.define('constructor-browse', ConstructorBrowse);
